/**
 * Business Service
 * 
 * Handles business profile and management operations including business information
 * retrieval and updates, verification status checks, business owner management,
 * and bank account operations for business accounts.
 */

import { apiRequest } from '@/shared/services/apiClient.js';
import logger from '@/shared/utils/logger.js';

export const businessService = {
  /**
   * Retrieves the verification status for a business account.
   * @param {string} businessId - The unique identifier of the business
   * @returns {Promise<Object>} Business verification status including level and status
   */
  async getBusinessVerificationStatus(businessId) {
    logger.debug('Fetching business verification status');

    const verificationData = await apiRequest(`/api/auth/business/${businessId}/verification-status`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    logger.debug('Business verification status fetched');
    return verificationData;
  },
  /**
   * Retrieves complete business information including profile details and settings.
   * @param {string} businessId - The unique identifier of the business
   * @returns {Promise<Object>} Business information data
   */
  async getBusinessInfo(businessId) {
    if (!businessId || !String(businessId).trim()) { throw new Error('Business ID is required'); }

    logger.debug('Fetching business information', { businessId });

    const res = await apiRequest(`/api/auth/business/${businessId}/business-info`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    logger.debug('Business information fetched', { response: res });

    // If response has data, return it
    if (res && res.data) {
      return res.data;
    }

    // If response is the data itself (no wrapper), return it
    if (res && typeof res === 'object') {
      return res;
    }

    throw new Error('Failed to fetch business information');
  },

  /**
   * Updates business information with validation of required fields.
   * Validates business ID format and required fields before submission.
   * @param {string} businessId - The unique identifier of the business
   * @param {Object} businessInfo - Business information to update including trading name, description, staff size, etc.
   * @returns {Promise<Object>} API response confirming the update
   * @throws {Error} If required fields are missing or business ID is invalid
   */
  async updateBusinessInfo(businessId, businessInfo) {
    logger.debug('Updating business information', { businessId, businessInfo });

    if (!businessId || !String(businessId).trim()) { throw new Error('Business ID is required'); }

    const tradingName = businessInfo.tradingName;
    const description = businessInfo.description;
    const staffSize = businessInfo.staffSize;
    const expectedMonthlyRevenue = businessInfo.expectedMonthlyRevenue;
    const tinNumber = businessInfo.tinNumber;
    const registrationType = businessInfo.registrationType;

    if (!tradingName || !String(tradingName).trim()) { throw new Error('Trading name is required'); }
    if (!tinNumber || !String(tinNumber).trim()) { throw new Error('TIN number is required'); }
    if (!registrationType || !String(registrationType).trim()) { throw new Error('Registration type is required'); }

    const requestBody = {
      trading_name: String(tradingName).trim(),
      tin_number: String(tinNumber).trim(),
      registration_type: String(registrationType).trim().replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    };

    // Add optional fields only if they have values
    if (description && String(description).trim()) {
      requestBody.description = String(description).trim();
    }
    if (staffSize && String(staffSize).trim()) {
      requestBody.staff_size = String(staffSize).trim();
    }
    if (expectedMonthlyRevenue && !isNaN(parseInt(expectedMonthlyRevenue)) && parseInt(expectedMonthlyRevenue) > 0) {
      requestBody.expected_monthly_revenue = parseInt(expectedMonthlyRevenue);
    }

    // Add optional contact information fields
    if (businessInfo.businessEmail) { requestBody.business_email = String(businessInfo.businessEmail).trim(); }
    if (businessInfo.supportEmail) { requestBody.support_email = String(businessInfo.supportEmail).trim(); }
    if (businessInfo.disputesEmail) { requestBody.disputes_email = String(businessInfo.disputesEmail).trim(); }
    if (businessInfo.businessPhone) {
      // Clean phone number: remove leading zeros and ensure proper format
      let phoneNumber = String(businessInfo.businessPhone).trim();
      // Remove any '+' signs
      phoneNumber = phoneNumber.replace(/^\+/, '');
      // Remove leading zeros (phone should start with country code, not zero)
      phoneNumber = phoneNumber.replace(/^0+/, '');
      // Remove any non-digit characters
      phoneNumber = phoneNumber.replace(/[^\d]/g, '');
      requestBody.business_phone = phoneNumber;
    }
    if (businessInfo.websiteUrl) { requestBody.website_url = String(businessInfo.websiteUrl).trim(); }

    // Add optional address information fields
    if (businessInfo.registeredOffice) { requestBody.registered_office = String(businessInfo.registeredOffice).trim(); }
    if (businessInfo.operatingAddress) { requestBody.operating_address = String(businessInfo.operatingAddress).trim(); }
    if (businessInfo.gpsAddress) { requestBody.gps_address = String(businessInfo.gpsAddress).trim(); }

    // Add optional business details fields
    if (businessInfo.industry) { requestBody.industry = String(businessInfo.industry).trim(); }
    if (businessInfo.category) { requestBody.category = String(businessInfo.category).trim(); }
    if (businessInfo.registrationNumber) { requestBody.registration_number = String(businessInfo.registrationNumber).trim(); }
    if (businessInfo.dateOfIncorporation) {
      // Convert date to ISO-8601 DateTime format (YYYY-MM-DDTHH:mm:ss.sssZ)
      // Prisma expects DateTime format, not integer timestamp
      const dateStr = String(businessInfo.dateOfIncorporation).trim();
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        // Ensure the date is in ISO format for Prisma DateTime field
        try {
          requestBody.date_of_incorporation = date.toISOString();
        } catch (e) {
          logger.warn('Failed to convert dateOfIncorporation to ISO string', { dateStr, error: e });
          // Skip the field if conversion fails
        }
      }
    }

    logger.debug('Business: Final JSON payload being sent to server', { requestBody });

    const res = await apiRequest(`/api/auth/business/${businessId}/business-info`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    logger.debug('Business information updated', { response: res });

    if (res && (res.code === '01' || res.code === '00')) { return res.data || res; }
    throw new Error((res && res.message) || 'Failed to update business information');
  },

  /**
   * Adds a new business owner to the business account.
   * Validates business ID and owner full name before submission.
   * @param {string} businessId - The unique identifier of the business
   * @param {Object} ownerData - Business owner information including full name and ownership details
   * @returns {Promise<Object>} API response confirming owner addition
   * @throws {Error} If business ID or owner name is invalid
   */
  async addBusinessOwner(businessId, ownerData) {
    logger.debug('Adding business owner');

    if (!businessId || typeof businessId !== 'string' || businessId.trim() === '') {
      throw new Error('Valid business ID is required');
    }

    if (!ownerData.full_name || ownerData.full_name.trim() === '') {
      throw new Error('Full name is required');
    }

    // Ensure date fields are properly formatted as ISO strings
    // The backend expects ISO date strings, not Date objects
    // IMPORTANT: Never send Date objects - always convert to ISO strings
    const sanitizedOwnerData = { ...ownerData };

    // Helper function to safely convert any date value to ISO string
    const convertToISOString = (value) => {
      // If null, undefined, or empty string, return null (will be removed)
      if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
        return null;
      }

      // If it's already a valid ISO string, return it
      if (typeof value === 'string' && value.includes('T')) {
        try {
          const testDate = new Date(value);
          if (!isNaN(testDate.getTime())) {
            return value; // Valid ISO string
          }
        } catch (e) {
          // Invalid ISO string
          return null;
        }
      }

      // If it's a Date object, convert to ISO string
      if (value instanceof Date) {
        if (isNaN(value.getTime())) {
          return null; // Invalid date
        }
        try {
          return value.toISOString();
        } catch (e) {
          logger.warn('Failed to convert Date to ISO string', { value, error: e });
          return null;
        }
      }

      // If it's a string in YYYY-MM-DD format, convert to ISO
      if (typeof value === 'string') {
        try {
          // Handle YYYY-MM-DD format
          const dateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
          if (dateMatch) {
            const date = new Date(value + 'T00:00:00.000Z');
            if (!isNaN(date.getTime())) {
              try {
                return date.toISOString();
              } catch (e) {
                logger.warn('Failed to convert date string to ISO string', { value, error: e });
                return null;
              }
            }
          }
        } catch (e) {
          logger.warn('Invalid date format', { value });
        }
      }

      // If we get here, it's not a valid date format
      return null;
    };

    // Convert any date fields to ISO strings if they're Date objects
    const dateFields = ['dob', 'id_issue_date', 'id_expiry_date'];
    dateFields.forEach(field => {
      const convertedValue = convertToISOString(sanitizedOwnerData[field]);
      if (convertedValue === null) {
        // Remove invalid or empty date fields
        delete sanitizedOwnerData[field];
      } else {
        // Set the ISO string value
        sanitizedOwnerData[field] = convertedValue;
      }
    });

    // Final check: ensure all date fields are either valid ISO strings or completely removed
    // Also ensure NO Date objects are sent (they can't be serialized properly)
    const finalSanitizedData = {};
    Object.keys(sanitizedOwnerData).forEach(key => {
      const value = sanitizedOwnerData[key];

      // Never send Date objects - always convert to ISO string
      if (value instanceof Date) {
        if (!isNaN(value.getTime())) {
          try {
            finalSanitizedData[key] = value.toISOString();
          } catch (e) {
            logger.warn('Failed to convert Date to ISO string in final sanitization', { key, value, error: e });
            // Skip invalid dates
            return;
          }
        }
        // Skip invalid dates
        return;
      }

      // For date fields, ensure they're valid ISO strings
      if (dateFields.includes(key)) {
        if (typeof value === 'string' && value.includes('T')) {
          try {
            const testDate = new Date(value);
            if (!isNaN(testDate.getTime())) {
              finalSanitizedData[key] = value; // Valid ISO string
            }
            // Skip invalid dates
          } catch (e) {
            logger.warn('Removing invalid date field', { key, value });
            // Skip invalid dates
          }
        }
        // Skip non-string or non-ISO date fields
        return;
      }

      // For non-date fields, copy as-is (but skip null/undefined)
      if (value !== null && value !== undefined) {
        finalSanitizedData[key] = value;
      }
    });

    const requestBody = {
      owners: [finalSanitizedData]
    };

    logger.debug('Business: Owner data being sent', {
      requestBody,
      dateFields: {
        dob: finalSanitizedData.dob,
        dobType: typeof finalSanitizedData.dob,
        id_issue_date: finalSanitizedData.id_issue_date,
        id_expiry_date: finalSanitizedData.id_expiry_date
      }
    });

    const response = await apiRequest(`/auth/business/${businessId}/owners`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    logger.debug('Business owner added');
    return response;
  },

  /**
   * Retrieves all business owners for a business account.
   * @param {string} businessId - The unique identifier of the business
   * @returns {Promise<Array>} Array of business owners
   * @throws {Error} If business ID is invalid
   */
  async getBusinessOwners(businessId) {
    logger.debug('Fetching business owners');

    if (!businessId || typeof businessId !== 'string' || businessId.trim() === '') {
      throw new Error('Valid business ID is required');
    }

    try {
      const response = await apiRequest(`/auth/business/${businessId}/owners`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      logger.debug('Business owners fetched');

      // Handle different response formats
      let owners = [];
      if (response?.data) {
        owners = Array.isArray(response.data) ? response.data : (Array.isArray(response.data.owners) ? response.data.owners : []);
      } else if (Array.isArray(response)) {
        owners = response;
      } else if (response?.owners && Array.isArray(response.owners)) {
        owners = response.owners;
      }

      // Sanitize owners data to ensure all date fields are properly formatted
      // This prevents issues if backend returns invalid date formats
      return owners.map(owner => {
        if (!owner || typeof owner !== 'object') {
          return owner;
        }

        const sanitizedOwner = { ...owner };
        const dateFields = ['dob', 'id_issue_date', 'id_expiry_date', 'created_at', 'updated_at'];

        dateFields.forEach(field => {
          if (sanitizedOwner[field] !== undefined && sanitizedOwner[field] !== null) {
            // If it's a Date object, convert to ISO string
            if (sanitizedOwner[field] instanceof Date) {
              if (!isNaN(sanitizedOwner[field].getTime())) {
                try {
                  sanitizedOwner[field] = sanitizedOwner[field].toISOString();
                } catch (e) {
                  logger.warn('Failed to convert Date to ISO string in owner processing', { field, value: sanitizedOwner[field], error: e });
                  delete sanitizedOwner[field];
                }
              } else {
                delete sanitizedOwner[field];
              }
            }
            // If it's not a string and not a Date, try to convert or remove
            else if (typeof sanitizedOwner[field] !== 'string') {
              // Try to convert number (timestamp) to ISO string
              if (typeof sanitizedOwner[field] === 'number' && !isNaN(sanitizedOwner[field])) {
                try {
                  const date = new Date(sanitizedOwner[field]);
                  if (!isNaN(date.getTime())) {
                    try {
                      sanitizedOwner[field] = date.toISOString();
                    } catch (e) {
                      logger.warn('Failed to convert date to ISO string for owner field', { field, value: sanitizedOwner[field], error: e });
                      delete sanitizedOwner[field];
                    }
                  } else {
                    delete sanitizedOwner[field];
                  }
                } catch (e) {
                  logger.warn('Failed to convert timestamp to date for owner field', { field, value: sanitizedOwner[field] });
                  delete sanitizedOwner[field];
                }
              } else {
                // Remove invalid date fields
                logger.warn('Invalid date format for owner field', { field, value: sanitizedOwner[field], valueType: typeof sanitizedOwner[field] });
                delete sanitizedOwner[field];
              }
            }
            // If it's a string but not in ISO format, try to convert
            else if (typeof sanitizedOwner[field] === 'string' && !sanitizedOwner[field].includes('T')) {
              try {
                const date = new Date(sanitizedOwner[field] + 'T00:00:00.000Z');
                if (!isNaN(date.getTime())) {
                  try {
                    sanitizedOwner[field] = date.toISOString();
                  } catch (e) {
                    logger.warn('Failed to convert date string to ISO string for owner field', { field, value: sanitizedOwner[field], error: e });
                    delete sanitizedOwner[field];
                  }
                } else {
                  delete sanitizedOwner[field];
                }
              } catch (e) {
                logger.warn('Failed to parse date string for owner field', { field, value: sanitizedOwner[field] });
                delete sanitizedOwner[field];
              }
            }
          }
        });
        return sanitizedOwner;
      });
    } catch (error) {
      // Handle backend errors gracefully
      if (error?.code === '07' || error?.message?.includes('toISOString') || error?.message?.includes('dob')) {
        logger.error('Backend error processing owner date fields. This may indicate invalid data in the database', { error });
        // Return empty array instead of throwing to prevent UI crashes
        return [];
      }

      // Also catch it from the raw response data if available
      if (error?.data?.message && (error.data.message.includes('toISOString') || error.data.message.includes('dob'))) {
        logger.error('Backend returned data error for owner dates', { error });
        return [];
      }

      // Re-throw other errors
      throw error;
    }

  },

  /**
   * Deletes a business owner from a business account.
   * @param {string} businessId - The unique identifier of the business
   * @param {string} ownerId - The unique identifier of the owner
   * @returns {Promise<Object>} API response confirming deletion
   */
  async deleteBusinessOwner(businessId, ownerId) {
    logger.debug('Deleting business owner', { businessId, ownerId });

    if (!businessId || !String(businessId).trim()) {
      throw new Error('Business ID is required');
    }
    if (!ownerId || !String(ownerId).trim()) {
      throw new Error('Owner ID is required');
    }

    const response = await apiRequest(`/api/auth/business/${businessId}/owners/${ownerId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    logger.debug('Business owner deleted', { response });
    return response;
  },

  /**
   * Uploads a business logo image
   * @param {string} businessId - The unique identifier of the business
   * @param {File} logoFile - Logo image file (PNG, JPG, JPEG, SVG)
   * @returns {Promise<Object>} Upload response with logo URL
   * @throws {Error} If business ID or logo file is invalid
   */
  async uploadBusinessLogo(businessId, logoFile) {
    logger.debug('Uploading business logo', { businessId, fileName: logoFile?.name });

    if (!businessId || !String(businessId).trim()) {
      throw new Error('Business ID is required');
    }

    if (!logoFile) {
      throw new Error('Logo file is required');
    }

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!allowedTypes.includes(logoFile.type)) {
      throw new Error('Invalid file type. Only PNG, JPG, JPEG, and SVG files are allowed');
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (logoFile.size > maxSize) {
      throw new Error('File size must be less than 5MB');
    }

    const formData = new FormData();
    formData.append('file', logoFile);
    formData.append('businessId', businessId);

    // Shotgun approach: satisfying potential missing backend variables
    // derived from "userKey is not defined" error in gatewayControllerv2.js
    let userId = '';
    try {
      const userData = JSON.parse(localStorage.getItem('userData'));
      userId = userData?.id || '';
      if (userId) {
        formData.append('userId', userId);
        formData.append('user_id', userId);
      }
    } catch (e) { }

    // Some gateways might expect businessId as userKey or vice versa
    formData.append('userKey', businessId);

    // Hit the endpoint with businessId in query as well, just in case
    const response = await apiRequest(`/api/auth/profile/add-logo?businessId=${businessId}`, {
      method: 'POST',
      body: formData,
      headers: userId ? { 'x-user-id': userId } : {},
    });

    logger.debug('Business logo uploaded successfully', { response });
    return response;
  },

  /**
   * Retrieves the business logo URL
   * @param {string} businessId - The unique identifier of the business
   * @returns {Promise<Object>} Logo data with URL
   * @throws {Error} If business ID is invalid
   */
  async getBusinessLogo(businessId) {
    logger.debug('Fetching business logo', { businessId });

    if (!businessId || !String(businessId).trim()) {
      throw new Error('Business ID is required');
    }

    const response = await apiRequest(`/api/auth/profile/logo-url?businessId=${businessId}`, {
      method: 'GET',
    });

    logger.debug('Business logo fetched', { response });
    return response;
  },

  /**
   * Deletes a business account.
   * @param {string} businessId - The unique identifier of the business
   * @returns {Promise<Object>} API response confirming deletion
   */
  async deleteBusiness(businessId) {
    logger.debug('Deleting business account', { businessId });

    if (!businessId || !String(businessId).trim()) {
      throw new Error('Business ID is required');
    }

    const response = await apiRequest(`/api/auth/business/${businessId}/delete`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    logger.debug('Business account deleted', { response });
    return response;
  },

  /**
   * Retrieves the current charge bearer setting for the authenticated business.
   * @returns {Promise<Object>} Charge bearer data, e.g. { charge_bearer: 'business' | 'customer' }
   */
  async getChargeBearer() {
    logger.debug('Fetching charge bearer setting');
    // Always send Authorization header (apiClient does this by default, but explicit for clarity)
    const response = await apiRequest('/api/auth/charge-bearer', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    logger.debug('Charge bearer setting fetched', { response });
    return response;
  },

  /**
   * Updates the charge bearer setting for the authenticated business.
   * @param {'business'|'customer'} chargeBearer - Who bears the transaction charge
   * @returns {Promise<Object>} API response confirming the update
   */
  async updateChargeBearer(chargeBearer) {
    if (!['business', 'customer'].includes(chargeBearer)) {
      throw new Error("charge_bearer must be 'business' or 'customer'");
    }
    logger.debug('Updating charge bearer setting', { chargeBearer });
    // Map to API values
    const apiValue = chargeBearer === 'business' ? 'initiator' : 'non-initiator';
    const response = await apiRequest('/api/auth/charge-bearer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bearer: apiValue }),
    });
    logger.debug('Charge bearer setting updated', { response });
    return response;
  },
};

export default businessService;
